import React from 'react';
import { connect } from 'react-redux';
import { MuiThemeProvider, createTheme } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import { resultsConfigSelector, quoteFormSelector } from 'ctm-selectors';
import { withRules, SimpleFlexbox, ModalCloseIcon } from 'ctm-components';
import { pageContext } from 'ctm-util-functions';
import styled from 'styled-components';

// This is a ANON PREFILL 2.0 only component.
const theme = createTheme({
    overrides: {
        MuiPaper: {
            root: {
                padding: '2rem',
                // To fix the absolute-positioned close icon being cut off
                overflowY: 'unset !important'
            }
        }
    }
});

const Title = styled.h1`
    font-size: 1.6rem;
    color: var( --brand-primary );
    font-weight: 600;
    margin-bottom: 0.5rem;
    line-height: 20px;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
`;

const Text = styled.p`
    font-size: 1.4rem;
    line-height: 1.7rem;
    color: var(--brand-base);
    font-weight: 400;
    margin-bottom: 0.5rem;
`;

const Button = styled.button`
    background: var(--brand-base-0);
    border: ${(props) => props.border};
    box-sizing: border-box;
    border-radius: 4px;
    font-size: 1.4rem;
    font-weight: 500;
    color: var(--brand-primary);
    padding: 14px 24px;
    height: 4.4rem;
    &:hover {
        cursor: pointer;
    }
    display: flex;
    align-items: center;
    justify-content: center;
    width: 12rem;
`;

const _StartANewQuoteModal = (props) => {
    const { UpdateQuoteModalIsOpen, closeModal, resetHasSavedResults, resetJourneyPayload, saveRedbookCode, carRedbookCode } = props;
    const renderCloseIcon = () => {
        const closeIconProps = { onClickFunction: closeModal };
        return React.createElement(ModalCloseIcon, closeIconProps);
    };
    const isXs = pageContext.getProps().pageState.breakpoint === 'xs';

    const handleRedirect = () => {
        closeModal();
        saveRedbookCode(carRedbookCode);
        resetHasSavedResults();
        resetJourneyPayload();
        pageContext.getProps().browserHistory.push('/car-insurance/journey/prefill_check');
    };

    return (
        <MuiThemeProvider theme={theme}>
            <Dialog fullwidth open={UpdateQuoteModalIsOpen} disableBackdropClick disableEscapeKeyDown>
                <SimpleFlexbox display="flex" column>
                    <Title>This will clear all your details and quotes.</Title>
                    <Text>You can edit your info without starting over.</Text>
                    <Text>Are you sure you want to start over?</Text>
                </SimpleFlexbox>
                {renderCloseIcon()}
                <ButtonContainer>
                    <Button id="savedResults.StartANewQuoteModalDialogueButton.cancel" onClick={closeModal} type="button">
                        Cancel
                    </Button>
                    <Button style={{ width: isXs ? '16rem' : '21rem' }}
                        id="savedResults.StartANewQuoteModalDialogueButton.update"
                        type="button"
                        border={'1px solid var(--brand-primary)'}
                        onClick={() => {
                            handleRedirect();
                        }}
                    >
                        Start a new quote
                    </Button>
                </ButtonContainer>
            </Dialog>
        </MuiThemeProvider>
    );
};

const mapDispatchToProps = (dispatch) => ({

    resetHasSavedResults: () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: false
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'lastViewedPage',
            value: 'results'
        });
    },
    resetJourneyPayload: () => {
        // Reset prefill values from any previous journeys within the session
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'hasBeenPrefilled',
            value: false
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'hasSeenResults',
            value: false
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: false
        });
        // Clear journey + form + results
        dispatch({
            type: 'SET_JOURNEY_PAYLOAD',
            payload: { }
        });
        dispatch({
            type: 'RESET_RESULTS'
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'anyPreviousClaimsCount',
            value: null
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'coverDetail',
            value: {
                useType: null,
                previousInsurer: null,
                requestedExcess: null,
                commencementDate: null,
                currentlyInsured: null,
                annualKilometres: null,
                driver: {
                    lastName: null,
                    gender: null,
                    claimsDetails: null,
                    licenceAge: null,
                    dob: null,
                    comprehensiveInsured: null,
                    comprehensiveInsuredYears: null,
                    anyPreviousClaims: null,
                    firstName: null,
                    employmentStatus: null
                },
                ownsHome: null,
                vehicle: {
                    body: null,
                    redbookCodeLong: null,
                    badge: null,
                    transmission: null,
                    nonStandardAccessories: null,
                    redbookCode: null,
                    modified: null,
                    model: null,
                    glassCode: null,
                    colour: null,
                    nviCode: null,
                    make: null,
                    state: null,
                    alarmFitted: null,
                    factoryOptions: null,
                    marketValue: null,
                    damaged: null,
                    fuel: null,
                    immobiliserFitted: null,
                    hasOtherAccessories: null,
                    rego: null,
                    description: null,
                    year: null,
                    securityFeatures: null
                },
                passengersPayment: null,
                goodsPayment: null,
                coverType: null,
                hasYoungerDriver: null,
                financeType: null,
                overnightParking: {
                    parkingType: null,
                    address: {
                        unitType: null,
                        unitNumber: null,
                        buildingName: null,
                        streetNumber: null,
                        streetName: null,
                        state: null,
                        suburb: null,
                        postcode: null
                    }
                },
                youngestDriver: {
                    licenceAge: null,
                    dob: null,
                    gender: null
                },
                driverOption: null,
                ownsAnotherCar: null
            }
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'applicant',
            value: {
                optInPhone: null,
                optInEmail: null,
                optInPrivacy: null,
                email: null,
                phone: null,
                mobile: null,
                lastName: null,
                firstName: null
            }
        });
        // Clear form specifics
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'helpers.overnightParkingAddressLabel',
            value: null
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'helpers.regoLabel',
            value: null
        });
        dispatch({
            type: 'RESET_TOUCHES',
            stateKey: 'vertical.quote',
            identifiers: [
                'coverDetail.annualKilometres',
                'coverDetail.driver.dob',
                'coverDetail.driver.licenceAge',
                'coverDetail.overnightParking.address.postcode',
                'applicant.firstName',
                'applicant.lastName',
                'helpers.overnightParkingPostcode',
                'helpers.overnightParkingAddressLabel',
                'anyPreviousClaimsCount',
                'helpers.regoLabel',
                'firstPage.vehicle.rego'
            ]
        });
    },
    saveRedbookCode: (redbookCode) => {
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'helpers.preventPrefill.redbookCode',
            value: redbookCode
        });
    }
});

const mapStateToProps = (state) => {
    const isModalOpen = resultsConfigSelector(state).getIn(['helpers', 'startANewQuoteModal']);
    const carRedbookCode = quoteFormSelector(state).getIn(['coverDetail', 'vehicle', 'redbookCode']);
    return { UpdateQuoteModalIsOpen: isModalOpen ? isModalOpen.open : false, carRedbookCode };
};

const StartANewQuoteModal = withRules(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(_StartANewQuoteModal)
);

export { _StartANewQuoteModal, StartANewQuoteModal };
